import PropTypes from 'prop-types';
import { useState } from "react"

// material-ui
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, Link, Stack, TextField, Select, MenuItem, InputLabel } from "@mui/material"


/**
 * Checkbox group input field for filter.
 */
export const FilterCheckboxGroup = ({ name, data, filters, label, onChange }) => {
    const [selected, setSelected] = useState(filters.filter((item) => item.field == name).map((item) => item.value))
    const labelid = 'filter-' + name + '-label'

    const onDataChange = (e) => {
        const vals = selected.filter((item) => item !== e.target.value)
        if (e.target.checked) {
            vals.push(e.target.value)
        }
        setSelected(vals)

        const res = data.filter((item) => vals.includes(item.value)).map((item) => {
            item['field'] = name
            return item
        })
        onChange(name, res)
    }

    return (
        <FormControl fullWidth>
            <Stack direction="row" justifyContent="space-between">
                <FormLabel id={labelid}>{label}</FormLabel>
                {selected.length > 0 && <Link component="button" variant="body2" size="small" onClick={() => {
                    setSelected([])
                    onChange(name, [])
                }}>Clear</Link>}
            </Stack>
            <FormGroup sx={{ my: 1, '& .MuiCheckbox-root': { py: '5px' } }}>
                {data && data.map((val) =>
                    <FormControlLabel key={val.value} value={val.value} control={
                        <Checkbox size="small" checked={selected.includes(val.value)} title={val.text} onChange={onDataChange} />
                    } label={val.text} />
                )}
            </FormGroup>
        </FormControl>
    )
}

/**
 * Radio group input field for filter.
 */
export const FilterRadioGroup = ({ name, data, filters, label = '', onChange }) => {
    const slist = filters.filter((item) => item.field == name).map((item) => item.value)
    const [selected, setSelected] = useState(slist.length > 0 ? slist[0] : '')
    const labelid = 'filter-' + name + '-label'

    const onDataChange = (e) => {
        setSelected(e.target.value)
        const res = data.find((item) => item.value == e.target.value)
        res['field'] = name
        onChange(name, [res])
    }

    return (
        <FormControl fullWidth>
            <Stack direction="row" justifyContent="space-between">
                <FormLabel id={labelid}>{label}</FormLabel>
                {selected != '' && <Link component="button" variant="body2" size="small" onClick={() => {
                    setSelected('')
                    onChange(name, [])
                }}>Clear</Link>}
            </Stack>
            <RadioGroup defaultValue="" name={name} sx={{ my: 1, '& .MuiRadio-root': { py: '5px' } }}>
                {data && data.map((val) =>
                    <FormControlLabel key={val.value} value={val.value} control={
                        <Radio size="small" checked={val.value == selected} onChange={onDataChange} />
                    } label={val.text} />
                )}
            </RadioGroup>
        </FormControl>
    )
}

/**
 * Auto complete select input field for filter.
 */
export const FilterAutoComplete = ({ name, filters, label = '', onChange, field }) => {
 
    const fieldFilters = Array.isArray(filters) ? filters.filter(item => item.field === name) : [];
    let selected = null;
 
    if (fieldFilters.length > 0) {
        selected = fieldFilters.map(item => ({
            id: item.value ?? item.id,
            name: item.text ?? item.name
        }));
    }
 
    let value = Array.isArray(selected) ? selected : selected ? [selected] : [];
 
    const formatData = (input, name) => {
        const items = Array.isArray(input) ? input : [input];
 
        return items.map(item => ({
            value: item.id,
            text: item.title ? item.title : item.name,
            field: name
        }));
    };
 
    const onSelected = (fieldname, data) => {
        if (data == null) {
            onChange(name, [])
        } else {
            onChange(name, formatData(data, name));
        }
    }
 
    return (
        <FormControl fullWidth sx={{ pb: 1 }}>
            <FormLabel sx={{ pb: 1 }}>{label}</FormLabel>
            {field(value, onSelected)}
        </FormControl>
    )
}

/**
 * Date picket input field for filter.
 */
export const FilterDatePicker = ({ name, filters, onChange, label = '' }) => {
    const selected = filters.find((item) => item.field === name);
    const selecteddate = selected ? new Date(selected.value) : null;

    const handleDateChange = (date) => {
        if (!date) {
            onChange(name, []);
        } else {
            onChange(name, [{ value: date.toISOString(), text: date.toDateString(), field: name }]);
        }
    };

    const clearDate = () => {
        onChange(name, []);
    };

    return (
        <FormControl fullWidth>
            <Stack direction="row" justifyContent="space-between">
                <FormLabel>{label}</FormLabel>
                {selecteddate && (
                    <Link component="button" variant="body2" size="small" onClick={clearDate}>
                        Clear
                    </Link>
                )}
            </Stack>
            <TextField fullWidth type="date" sx={{ pt: 2 }}
                value={selecteddate ? selecteddate.toISOString().substr(0, 10) : ''}
                onChange={(e) => handleDateChange(new Date(e.target.value))}
                InputLabelProps={{
                    shrink: true,
                }}
            />
        </FormControl>
    );
}

/**
 * Select input field for filter.
 */
export const FilterSelect = ({ name, data, filters, label = '', onChange, placeholder = 'Select...' }) => {
  const slist = filters.filter((item) => item.field == name).map((item) => item.value)
  const [selected, setSelected] = useState(slist.length > 0 ? slist[0] : '')
  const labelid = 'filter-' + name + '-label'

  const onDataChange = (e) => {
    const value = e.target.value
    setSelected(value)
    
    if (value === '') {
      onChange(name, [])
    } else {
      const res = data.find((item) => item.value == value)
      res['field'] = name
      onChange(name, [res])
    }
  }

  return (
    <FormControl fullWidth>
        <Stack direction="row" justifyContent="space-between">
            <FormLabel id={labelid}>{label}</FormLabel>
            {selected != '' && <Link component="button" variant="body2" size="small" onClick={() => {
                setSelected('')
                onChange(name, [])
            }}>Clear</Link>}
        </Stack>
        <Select
            labelId={labelid}
            value={selected}
            label={label}
            onChange={onDataChange}
            MenuProps={{
                sx: {
                    zIndex: 1400
                }
            }}
            sx={{mt:2}}
        >
            <MenuItem value="">
            <em>{placeholder}</em>
            </MenuItem>
            {data && data.map((val) => (
            <MenuItem key={val.value} value={val.value}>
                {val.text}
            </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

FilterCheckboxGroup.propTypes = {
    name: PropTypes.string,
    data: PropTypes.object,
    filters: PropTypes.object,
    label: PropTypes.string,
    onChange: PropTypes.func,
};

FilterRadioGroup.propTypes = {
    name: PropTypes.string,
    data: PropTypes.object,
    filters: PropTypes.object,
    label: PropTypes.string,
    onChange: PropTypes.func,
};

FilterAutoComplete.propTypes = {
    name: PropTypes.string,
    filters: PropTypes.object,
    label: PropTypes.string,
    onChange: PropTypes.func,
    field: PropTypes.func,
};

FilterDatePicker.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
};

FilterSelect.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    option: PropTypes.object
};